import { Button, Modal, Form } from 'react-bootstrap'
import { useMutation } from '@tanstack/react-query'
import { emailReport } from '../../../../../../../utils/api/reportAPI'
import toasters from '../../../../../../../utils/toasters/Toasters'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { Formik, Field, ErrorMessage } from 'formik'
import {
    emailValidationSchema,
    emailFormInitialValues,
} from './validationAndInitialValues'

const EmailReportModal = ({
    show,
    onHide,
    reportData,
    reportCode,
    reportTableData,
}) => {
    const { mutate: sendEmail, isLoading } = useMutation(emailReport, {
        onSuccess: (data) => {
            toasters({ type: 'success', message: 'Report sent successfully!' })
            onHide()
        },
        onError: (error) => {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message ||
                    'Failed to send report. Please try again.',
            })
        },
    })

    const generateUserListPDF = (userListData, reportData) => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [297, 210],
            compress: true, // Enable PDF compression
        })

        doc.setFontSize(16) // Smaller title
        doc.text('User List Report', 14, 20)
        doc.setFontSize(10) // Smaller subtitle

        let currentY = 28

        // Add summary at the top
        const totalUsers = userListData?.length || 0
        const totalDevices =
            userListData?.reduce(
                (acc, user) => acc + (user?.devices_array?.length || 0),
                0
            ) || 0
        const averageDevicesPerUser =
            totalUsers > 0 ? (totalDevices / totalUsers).toFixed(2) : '0.00'
        doc.text(
            `Total Users: ${totalUsers} | Total Devices: ${totalDevices} | Average Devices per User: ${averageDevicesPerUser}`,
            14,
            currentY
        )
        currentY += 8

        userListData?.forEach((user, userIndex) => {
            // Compact user header
            doc.setFont(undefined, 'bold')
            doc.setFontSize(9)
            doc.text(
                `${user?.users_name_tx || ''} (${user?.email_tx || ''})`,
                14,
                currentY
            )
            doc.setFont(undefined, 'normal')
            currentY += 4

            const deviceRows = (user?.devices_array || []).map((device) => [
                device?.device_name_tx || '',
                device?.platform_tx || '',
                device?.type_tx || '',
                device?.creat_dt || '',
                device?.note_tx || '',
            ])

            if (deviceRows?.length > 0) {
                doc.autoTable({
                    head: [
                        [
                            'Device Name',
                            'Platform',
                            'Type',
                            'Created Date',
                            'Notes',
                        ],
                    ],
                    body: deviceRows,
                    startY: currentY,
                    theme: 'striped',
                    headStyles: {
                        fillColor: [0, 81, 242],
                        textColor: [255, 255, 255],
                        fontSize: 8,
                        cellPadding: 1,
                    },
                    bodyStyles: {
                        fontSize: 7,
                        cellPadding: 0.5,
                        lineWidth: 0.1,
                    },
                    styles: {
                        lineColor: [200, 200, 200],
                        lineWidth: 0.1,
                        cellPadding: 0.5,
                    },
                    columnStyles: {
                        0: { cellWidth: 55 },
                        1: { cellWidth: 45 },
                        2: { cellWidth: 45 },
                        3: { cellWidth: 55 },
                        4: { cellWidth: 65 },
                    },
                    didDrawPage: (data) => {
                        currentY = data.cursor.y + 5
                    },
                })
            } else {
                doc.setFontSize(8)
                doc.text('No devices found.', 14, currentY)
                currentY += 6
            }

            if (currentY > 190 && userIndex < userListData?.length - 1) {
                doc.addPage()
                currentY = 20 // Start higher on new page
            }
        })

        return doc.output('blob')
    }

    const generateCompactUserListPDF = (userListData, reportData) => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [297, 210],
            compress: true,
        })

        doc.setFontSize(14)
        doc.text('User List Report (Compact)', 14, 18)
        doc.setFontSize(8)

        const totalUsers = userListData?.length || 0
        const totalDevices =
            userListData?.reduce(
                (acc, user) => acc + (user?.devices_array?.length || 0),
                0
            ) || 0
        const averageDevicesPerUser =
            totalUsers > 0 ? (totalDevices / totalUsers).toFixed(2) : '0.00'
        doc.text(
            `Users: ${totalUsers} | Devices: ${totalDevices} | Avg Devices/User: ${averageDevicesPerUser}`,
            14,
            25
        )

        const tableRows =
            userListData?.map((user) => [
                user?.users_name_tx || '',
                user?.email_tx || '',
                user?.devices_array?.length || 0,
                user?.devices_array
                    ?.map((d) => d?.device_name_tx)
                    .filter(Boolean)
                    .join(', ') || 'No devices',
            ]) || []

        if (tableRows?.length > 0) {
            doc.autoTable({
                head: [['User Name', 'Email', 'Device Count', 'Device Names']],
                body: tableRows,
                startY: 32,
                theme: 'plain',
                headStyles: {
                    fillColor: [0, 81, 242],
                    textColor: [255, 255, 255],
                    fontSize: 7,
                    cellPadding: 0.5,
                },
                bodyStyles: {
                    fontSize: 6,
                    cellPadding: 0.2,
                },
                styles: {
                    lineColor: [150, 150, 150],
                    lineWidth: 0.1,
                    cellPadding: 0.2,
                },
                columnStyles: {
                    0: { cellWidth: 35 },
                    1: { cellWidth: 45 },
                    2: { cellWidth: 20 },
                    3: { cellWidth: 80 },
                },
            })
        }

        return doc.output('blob')
    }

    const generateCompactAppListPDF = (data) => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4',
            compress: true,
        })

        doc.setFontSize(14)
        doc.text('App List Report', 14, 18)

        const MAX_ROWS = 300 // hard limit for email safety

        const trimmedData = data.slice(0, MAX_ROWS)

        const columns = [
            'ID',
            'App Name',
            'Platform',
            'Package',
            'Group',
            'Score',
        ]

        const rows = trimmedData.map((row) => [
            row?.id ?? '',
            (row?.app_name_tx || '').slice(0, 30),
            row?.app_platform_tx ?? '',
            (row?.app_package_name || '').slice(0, 40),
            row?.app_group_name ?? '',
            row?.categorization?.['1']?.category_level_cd ||
                row?.other_devices_category_level_cd ||
                '-',
        ])

        doc.autoTable({
            head: [columns],
            body: rows,
            startY: 26,

            theme: 'plain',

            margin: {
                left: 10,
                right: 10,
            },

            tableWidth: 'auto',

            styles: {
                fontSize: 6,
                cellPadding: 1,
                overflow: 'linebreak',
                valign: 'top',
            },

            headStyles: {
                fontSize: 7,
                fillColor: [0, 81, 242],
                textColor: 255,
                halign: 'center',
            },

            columnStyles: {
                0: { cellWidth: 18 }, // ID
                1: { cellWidth: 55 }, // App Name
                2: { cellWidth: 30 }, // Platform
                3: { cellWidth: 85 }, // Package Name
                4: { cellWidth: 55 }, // Group
                5: { cellWidth: 34 }, // Score
            },
        })

        return doc.output('blob')
    }

    const handleSend = (values, { setSubmitting }) => {
        const emailList = values.emailAddresses
            .split(';')
            .map((email) => email.trim())
            .filter(Boolean)

        try {
            const maxSize = 10 * 1024 * 1024 // 10MB
            const normalizedReportCode = reportCode?.toString().toLowerCase()

            const isUserList = normalizedReportCode === 'userlist'

            const isAppList =
                normalizedReportCode === 'applist' ||
                normalizedReportCode === 'applistreport'

            const pdfBlob = generatePDFFile()

            if (pdfBlob.size > maxSize) {
                if (isUserList) {
                    const compactPdf = generateCompactUserListPDF(
                        reportTableData,
                        reportData
                    )

                    if (compactPdf.size <= maxSize) {
                        const formData = new FormData()
                        formData.append(
                            'file',
                            compactPdf,
                            `${reportCode}_compact.pdf`
                        )
                        formData.append('email', emailList.join(','))
                        formData.append(
                            'report_title',
                            'User List Report (Compact)'
                        )

                        sendEmail(formData)
                        setSubmitting(false)
                        return
                    }
                }

                if (isAppList) {
                    const compactPdf =
                        generateCompactAppListPDF(reportTableData)

                    if (compactPdf.size <= maxSize) {
                        const formData = new FormData()
                        formData.append(
                            'file',
                            compactPdf,
                            `${reportCode}_email_compact.pdf`
                        )
                        formData.append('email', emailList.join(','))
                        formData.append('report_title', 'App List Report')

                        sendEmail(formData)
                        setSubmitting(false)
                        return
                    }
                }

                toasters({
                    type: 'error',
                    message: `PDF is too large (${(
                        pdfBlob.size /
                        1024 /
                        1024
                    ).toFixed(2)}MB). Please reduce data or use Excel export.`,
                })

                setSubmitting(false)
                return
            }

            const formData = new FormData()

            let reportTitle = 'Report'

            if (normalizedReportCode === 'applist') {
                reportTitle = 'App List Report'
            } else if (normalizedReportCode === 'userlist') {
                reportTitle = 'User List Report'
            } else if (normalizedReportCode === 'incidentprocessedbywmrc') {
                reportTitle = 'Incident Processed By WMRC Report'
            } else if (normalizedReportCode === 'wmrcavgresponsetime') {
                reportTitle = 'WMRC Average Response Time Report'
            } else if (reportCode) {
                reportTitle = reportCode
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/^./, (s) => s.toUpperCase())
            }

            formData.append('file', pdfBlob, `${reportCode || 'report'}.pdf`)
            formData.append('email', emailList.join(','))
            formData.append('report_title', reportTitle)

            sendEmail(formData)
            setSubmitting(false)
        } catch (error) {
            toasters({
                type: 'error',
                message: `Failed to send report: ${error.message}`,
            })
            setSubmitting(false)
        }
    }

    const REPORT_COLUMN_STYLES = {
        scoreadjustmentanalysis: {
            0: { minCellWidth: 22 },
            1: { minCellWidth: 26 },
            2: { minCellWidth: 32 },
            3: { minCellWidth: 40 },
            4: { cellWidth: 'wrap' }, // long description
            5: { minCellWidth: 22, halign: 'center' },
            6: { minCellWidth: 22, halign: 'center' },
            7: { minCellWidth: 28, halign: 'center' },
        },

        initialreviewsbycongregation: {
            0: { minCellWidth: 45 },
            1: { cellWidth: 'wrap' },
            2: { minCellWidth: 35 },
            3: { minCellWidth: 35 },
        },

        localreviewsbycongregation: {
            0: { minCellWidth: 45 },
            1: { cellWidth: 'wrap' },
            2: { minCellWidth: 35 },
            3: { minCellWidth: 35 },
        },

        incidentprocessedbywmrc: {
            0: { minCellWidth: 45 },
            1: { minCellWidth: 35 },
            2: { minCellWidth: 30 },
            3: { minCellWidth: 30 },
        },

        localincidentsprocessedbywmrc: {
            0: { minCellWidth: 45 },
            1: { minCellWidth: 30 },
            2: { minCellWidth: 30 },
            3: { minCellWidth: 30 },
        },

        wmrcavgresponsetime: {
            0: { minCellWidth: 35 },
            1: { minCellWidth: 45 },
            2: { minCellWidth: 40 },
            3: { minCellWidth: 40 },
        },

        incidentcountwmrcgroup: {
            0: { minCellWidth: 22 },
            1: { minCellWidth: 40 },
            2: { minCellWidth: 35 },
            3: { minCellWidth: 35 },
            4: { minCellWidth: 25 },
            5: { minCellWidth: 30 },
            6: { minCellWidth: 30 },
        },

        default: {},
    }

    const generatePDFFile = () => {
        try {
            const normalizedTableData = Array.isArray(reportTableData)
                ? reportTableData
                : Array.isArray(reportTableData?.data)
                  ? reportTableData.data
                  : Array.isArray(reportTableData?.data?.data)
                    ? reportTableData.data.data
                    : []

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true,
            })

            let reportTitle = 'Report'
            // Normalize reportCode to handle case variations
            const normalizedReportCode = reportCode?.toString().toLowerCase()

            if (normalizedReportCode === 'incidentprocessedbywmrc') {
                reportTitle = 'Incident Processed By WMRC Report'
            } else if (
                normalizedReportCode === 'localIncidentsProcessedByWmrc'
            ) {
                reportTitle = 'Local Incidents Processed By WMRC Report'
            } else if (normalizedReportCode === 'incidentcountwmrcgroup') {
                reportTitle = 'Incident Count WMRC Group Report'
            } else if (normalizedReportCode === 'currentUserReport') {
                reportTitle = 'Current User Report'
            } else if (normalizedReportCode === 'DeviceHighScore') {
                reportTitle = 'Device High Score Report'
            } else if (normalizedReportCode === 'wmrcavgresponsetime') {
                reportTitle = 'WMRC Average Response Time Report'
            } else if (normalizedReportCode === 'urlsvsincidents') {
                reportTitle = 'URLs vs Incidents Report'
            } else if (reportCode) {
                let title = reportCode
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .replace(/^./, (str) => str.toUpperCase())

                if (!title.toLowerCase().endsWith('report')) {
                    title += ' Report'
                }

                reportTitle = title
            }

            doc.setFontSize(18)
            doc.text(reportTitle, 14, 22)
            doc.setFontSize(11)
            doc.setTextColor(100)

            // Add date range if available
            if (reportData?.startDate && reportData?.endDate) {
                doc.text(
                    `Report Date Range: ${reportData.startDate} - ${reportData.endDate}`,
                    14,
                    32
                )
            }

            if (normalizedTableData.length > 0) {
                let tableColumn = []
                let tableRows = []

                // Normalize reportCode for case-insensitive matching (reuse from above)
                const normalizedReportCodeForTable = reportCode
                    ?.toString()
                    .toLowerCase()

                if (normalizedReportCodeForTable === 'incidentanalysis') {
                    tableColumn = ['Types', 'Count']
                    tableRows = reportTableData?.map((item) => [
                        item?.name || item?.shortName || 0,
                        item?.count || 0,
                    ])
                } else if (
                    reportCode === 'userList' ||
                    reportCode === 'UserList'
                ) {
                    return generateUserListPDF(reportTableData, reportData)
                } else if (
                    reportCode === 'scoreChangeAnalysis' ||
                    reportCode === 'ScoreAdjustmentAnalysis'
                ) {
                    // Score Adjustment Analysis - exact same as downloadPDF
                    tableColumn = [
                        'Incident ID',
                        'Date',
                        'User Name',
                        'Incident Name',
                        'Incident Description',
                        'Score Change',
                        'Current Score',
                        'Current Status',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.incident_id || '',
                        item?.creat_dt || '',
                        item?.user_name_tx || '',
                        item?.incidentname_tx || '',
                        item?.description_tx || '',
                        item?.score_change_total ?? '',
                        item?.score_nr ?? '',
                        item?.ref_status_cd || '',
                    ])
                } else if (normalizedReportCodeForTable === 'urlsvsincidents') {
                    tableColumn = [
                        'Type',
                        'Event Count',
                        'Incident Count',
                        'URL to Incident Percentage',
                    ]

                    tableRows = normalizedTableData.map((item) => [
                        item?.name || '',
                        item?.eventcount || '',
                        item?.incidentcount || '',
                        item?.percentage ? `${item.percentage}%` : '',
                    ])
                } else if (
                    reportCode === 'systemReport' ||
                    reportCode === 'SystemReport'
                ) {
                    // System Report - exact same as downloadPDF
                    tableColumn = [
                        'Month',
                        'Users',
                        'Devices',
                        'Usage Total',
                        'Phone Devices',
                        'Incdt/Month',
                        'Percent',
                        'Send to Local',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.month || '',
                        item?.users || '',
                        item?.devices || '',
                        item?.usagetotal || '',
                        item?.phonedevices || '',
                        item?.incidents || '',
                        item?.percent
                            ? `${(item?.percent * 100).toFixed(2)}%`
                            : '',
                        item?.sent_to_local || '',
                    ])
                } else if (
                    // normalizedReportCodeForTable === 'currentUserReport'
                    reportCode === 'currentUserReport' ||
                    reportCode === 'CurrentUserReport'
                ) {
                    // Hardcoded column names to match your website UI
                    tableColumn = [
                        'Full Name',
                        'Company',
                        'Email',
                        'Phone',
                        'Congregation',
                        'Device Count',
                    ]

                    // Map data using the selectors from your 'const columns'
                    tableRows = normalizedTableData.map((item) => [
                        item?.name_tx || '',
                        item?.company_tx || '',
                        item?.email_tx || '',
                        item?.phone_tx || '',
                        item?.user_congregation || '',
                        item?.device_count ?? 0,
                    ])
                } else if (
                    reportCode === 'initialReviewsByCongregation' ||
                    reportCode === 'InitialReviewsByCongregation'
                ) {
                    // Initial Reviews By Congregation - use actual data structure
                    tableColumn = [
                        'Incident Name',
                        'Incident Description',
                        'Reviewed By',
                        'Created In',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.incidentname_tx || '',
                        item?.description_tx || '',
                        item?.user_who_changed_status_name_tx || '',
                        item?.group_name_tx || '',
                    ])
                } else if (
                    reportCode === 'localReviewsByCongregation' ||
                    reportCode === 'LocalReviewsByCongregation'
                ) {
                    // Initial Reviews By Congregation - use actual data structure
                    tableColumn = [
                        'Incident Name',
                        'Incident Description',
                        'Reviewed By',
                        'Created In',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.incidentname_tx || '',
                        item?.description_tx || '',
                        item?.user_who_changed_status_name_tx || '',
                        item?.group_name_tx || '',
                    ])
                } else if (
                    reportCode === 'alternateIncident' ||
                    reportCode === 'AlternateIncident'
                ) {
                    // Alternate Incident - use actual data structure
                    tableColumn = [
                        'Full Name',
                        'Email',
                        'Secondary Email',
                        'Alt Notification',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.name_tx || '',
                        item?.email_tx || '',
                        item?.email2_tx || '',
                        item?.sms_alert_yn || '',
                    ])
                } else if (
                    normalizedReportCodeForTable === 'specificappusersreport'
                ) {
                    tableColumn = ['Full Name', 'Email', 'Number of Devices']

                    tableRows = normalizedTableData.map((item) => [
                        item?.name_tx || '',
                        item?.email_tx || '',
                        item?.devices ?? 0,
                    ])
                } else if (
                    reportCode === 'wmrcResponseTimes' ||
                    reportCode === 'wmrcResponseTimes'
                ) {
                    // WMRC Response Time - use actual data structure
                    tableColumn = [
                        'All WMRC RESPONSE Time',
                        'Selected WMRC RESPONSE Time',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.td_allWmrcTime || 0,
                        item?.td_myWmrcTime || '',
                    ])
                } else if (
                    normalizedReportCodeForTable ===
                    'localincidentsprocessedbywmrc'
                ) {
                    // Local Incidents Processed By WMRC - use actual data structure matching downloadPDF
                    tableColumn = [
                        'Congregations',
                        'Total Incidents',
                        'Incident Closed',
                        'Open Incident',
                    ]
                    tableRows = normalizedTableData.map((item) => [
                        item?.name_tx || '',
                        item?.new_incidents || '',
                        item?.incident_closed || '',
                        item?.remaining_incidents || '',
                    ])
                } else if (
                    normalizedReportCodeForTable === 'incidentprocessedbywmrc'
                ) {
                    // Incident Processed By WMRC - use actual data structure matching downloadPDF
                    tableColumn = [
                        'Full Name',
                        'Sent To Local',
                        'Adjusted Score',
                        'Total',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.name_tx || '',
                        item?.sent_to_local || '',
                        item?.score_adjusted || '',
                        item?.total || 0,
                    ])
                } else if (
                    normalizedReportCodeForTable === 'wmrcavgresponsetime'
                ) {
                    // WMRC Average Response Time - match downloadPDF structure
                    tableColumn = [
                        'User ID',
                        'WMRC Name',
                        'Avg Response (Seconds)',
                        'Avg Response (Time)',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.user_id || '',
                        item?.wmrc_name || '',
                        item?.avg_response_seconds || '',
                        item?.avg_response_time || '',
                    ])
                } else if (
                    normalizedReportCodeForTable === 'openincidentswmrc'
                ) {
                    tableColumn = [
                        'WMRC User ID',
                        'WMRC User Name',
                        'Open Incidents',
                    ]
                    tableRows = normalizedTableData.map((item) => [
                        item?.wmrc_user_id || '',
                        item?.wmrc_user_name || '',
                        item?.open_incidents ?? 0,
                    ])
                } else if (normalizedReportCodeForTable === 'commentrequest') {
                    tableColumn = [
                        'User ID',
                        'WMRC Name',
                        'Comment Request Count',
                    ]

                    tableRows = normalizedTableData.map((item) => [
                        item?.user_id || '',
                        item?.wmrc_name || '',
                        item?.comment_request_count ?? 0,
                    ])
                } else if (
                    normalizedReportCodeForTable === 'incidentcountwmrcgroup'
                ) {
                    tableColumn = [
                        'Group ID',
                        'Congregation',
                        'WMRC Group',
                        'WMRC Name',
                        'Status',
                        'Year-Month',
                        'Incident Count',
                    ]

                    tableRows = normalizedTableData.map((item) => [
                        item?.group_id || '',
                        item?.group_name || '',
                        item?.wmrc_group || '',
                        item?.wmrc_group_contact_name || '',
                        item?.ref_status_cd || '',
                        item?.yearmonth || '',
                        item?.incident_count ?? 0,
                    ])
                } else if (normalizedReportCodeForTable === 'newuserreport') {
                    tableColumn = [
                        'User',
                        'Company',
                        'Address',
                        'Congregation',
                        'Devices',
                    ]

                    tableRows = normalizedTableData.map((item) => [
                        item?.name_tx || '',
                        item?.company_tx || '',
                        item?.address_tx || '',
                        item?.user_congregation || '',
                        item?.device_count ?? 0,
                    ])
                } else if (normalizedReportCodeForTable === 'devicehighscore') {
                    tableColumn = [
                        'Device Name',
                        'User Name',
                        'Congregation',
                        'One Month Back Data',
                        'Two Month Back Data',
                        'Three Month Back Data',
                        'Total Records',
                    ]

                    tableRows = normalizedTableData.map((item) => [
                        item?.name_tx || '',
                        item?.user_name || '',
                        item?.group_name || '',
                        item?.one_month_back_data ?? 0,
                        item?.two_month_back_data ?? 0,
                        item?.three_month_back_data ?? 0,
                        item?.total ?? 0,
                    ])
                } else if (
                    reportCode === 'incidentProcessedByInitialReview' ||
                    reportCode === 'IncidentProcessedByInitialReview'
                ) {
                    // Incident Processed By Initial Review - use actual data structure
                    tableColumn = [
                        'Incident ID',
                        'Processed Date',
                        'Status',
                        'Reviewer',
                    ]
                    tableRows = reportTableData?.map((item) => [
                        item?.incident_id || '',
                        item?.processed_date || '',
                        item?.status || '',
                        item?.reviewer || '',
                    ])
                } else {
                    // Generic fallback - try to detect structure
                    const firstItem = reportTableData[0]
                    if (firstItem) {
                        // Check if it looks like incident analysis data
                        if (
                            firstItem.hasOwnProperty('name') &&
                            firstItem.hasOwnProperty('count')
                        ) {
                            tableColumn = ['Types', 'Count']
                            tableRows = reportTableData?.map((item) => [
                                item?.name || '',
                                item?.count || '',
                            ])
                        }
                        // Check if it looks like user list data
                        else if (
                            firstItem.hasOwnProperty('users_name_tx') &&
                            firstItem.hasOwnProperty('email_tx')
                        ) {
                            tableColumn = [
                                'User Name',
                                'Email',
                                'Total Devices',
                            ]
                            tableRows = reportTableData?.map((item) => [
                                item?.users_name_tx || '',
                                item?.email_tx || '',
                                item?.devices_array?.length || 0,
                            ])
                        }
                        // Check if it looks like incident processed by wmrc data
                        else if (
                            firstItem.hasOwnProperty('name_tx') &&
                            firstItem.hasOwnProperty('sent_to_local') &&
                            firstItem.hasOwnProperty('score_adjusted') &&
                            firstItem.hasOwnProperty('total')
                        ) {
                            tableColumn = [
                                'Full Name',
                                'Sent To Local',
                                'Adjusted Score',
                                'Total',
                            ]
                            tableRows = reportTableData?.map((item) => [
                                item?.name_tx || '',
                                item?.sent_to_local || '',
                                item?.score_adjusted || '',
                                item?.total || 0,
                            ])
                        }
                        // Check if it looks like local incidents processed by wmrc data
                        else if (
                            firstItem.hasOwnProperty('name_tx') &&
                            firstItem.hasOwnProperty('new_incidents') &&
                            firstItem.hasOwnProperty('incident_closed') &&
                            firstItem.hasOwnProperty('remaining_incidents')
                        ) {
                            tableColumn = [
                                'Congregations',
                                'Total Incidents',
                                'Incident Closed',
                                'Open Incident',
                            ]
                            tableRows = reportTableData?.map((item) => [
                                item?.name_tx || '',
                                item?.new_incidents || '',
                                item?.incident_closed || '',
                                item?.remaining_incidents || '',
                            ])
                        }
                        // Check if it looks like WMRC Average Response Time data
                        else if (
                            firstItem.hasOwnProperty('user_id') &&
                            firstItem.hasOwnProperty('wmrc_name') &&
                            firstItem.hasOwnProperty('avg_response_seconds') &&
                            firstItem.hasOwnProperty('avg_response_time')
                        ) {
                            tableColumn = [
                                'User ID',
                                'WMRC Name',
                                'Avg Response (Seconds)',
                                'Avg Response (Time)',
                            ]
                            tableRows = reportTableData?.map((item) => [
                                item?.user_id || '',
                                item?.wmrc_name || '',
                                item?.avg_response_seconds || '',
                                item?.avg_response_time || '',
                            ])
                        }
                        // Generic fallback
                        else {
                            tableColumn = Object.keys(firstItem)
                            tableRows = reportTableData.map((item) =>
                                tableColumn.map((col) => item[col] || '')
                            )
                        }
                    }
                }

                if (tableColumn?.length > 0 && tableRows?.length > 0) {
                    // Define columnStyles for specific reports to match downloadPDF
                    let columnStyles = {}
                    if (
                        normalizedReportCodeForTable ===
                        'incidentprocessedbywmrc'
                    ) {
                        // Match columnStyles from downloadPDF
                        columnStyles = {
                            0: { cellWidth: 60 },
                            1: { cellWidth: 60 },
                            2: { cellWidth: 40 },
                            3: { cellWidth: 40 },
                        }
                    } else if (
                        normalizedReportCodeForTable ===
                        'localincidentsprocessedbywmrc'
                    ) {
                        // Match columnStyles from downloadPDF for Local Incidents Processed By WMRC
                        columnStyles = {
                            0: { cellWidth: 60 },
                            1: { cellWidth: 40 },
                            2: { cellWidth: 40 },
                            3: { cellWidth: 40 },
                        }
                    } else if (
                        normalizedReportCodeForTable === 'wmrcavgresponsetime'
                    ) {
                        // WMRC Average Response Time - no specific columnStyles needed, use default
                        columnStyles = {}
                    }

                    const normalizedCode = reportCode?.toString().toLowerCase()

                    doc.autoTable({
                        head: [tableColumn],
                        body: tableRows,
                        startY: 45,

                        margin: { top: 34, left: 10, right: 10 },
                        theme: 'grid',

                        tableWidth: 'auto',

                        headStyles: {
                            fillColor: [0, 81, 242],
                            textColor: [255, 255, 255],
                            fontSize: 10,
                            halign: 'center',
                            valign: 'middle',
                            cellPadding: 3,
                        },

                        bodyStyles: {
                            fontSize: 9,
                            cellPadding: 3,
                            valign: 'top',
                        },

                        styles: {
                            overflow: 'linebreak',
                            lineColor: [180, 180, 180],
                            lineWidth: 0.3,
                        },

                        columnStyles:
                            REPORT_COLUMN_STYLES[normalizedCode] ||
                            REPORT_COLUMN_STYLES.default,

                        pageBreak: 'auto',
                    })
                }
            } else {
                // No data available
                doc.text('No data available for this report.', 14, 50)
            }

            return doc.output('blob')
        } catch (error) {
            throw new Error('Failed to generate PDF: ' + error.message)
        }
    }

    const handleClose = () => {
        onHide()
    }

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Send Report via Email</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Formik
                    initialValues={emailFormInitialValues}
                    validationSchema={emailValidationSchema}
                    onSubmit={handleSend}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                    }) => (
                        <Form onSubmit={handleSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    Type in the email address(es) to which you
                                    want to send the report. Separate them with
                                    a semicolon.
                                </Form.Label>
                                <Field
                                    as={Form.Control}
                                    name="emailAddresses"
                                    type="textarea"
                                    rows={3}
                                    value={values.emailAddresses}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isLoading || isSubmitting}
                                    className={
                                        errors.emailAddresses &&
                                        touched.emailAddresses
                                            ? 'is-invalid'
                                            : ''
                                    }
                                />
                                <ErrorMessage
                                    name="emailAddresses"
                                    component="div"
                                    className="invalid-feedback"
                                />
                            </Form.Group>

                            <Modal.Footer className="px-0">
                                <Button
                                    variant="secondary"
                                    className="btn btn-outline-secondary"
                                    onClick={handleClose}
                                    disabled={isLoading || isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isLoading || isSubmitting}
                                >
                                    {isLoading || isSubmitting
                                        ? 'Sending...'
                                        : 'Send'}
                                </Button>
                            </Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    )
}

export default EmailReportModal
